import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  GroupDto,
  GroupInput,
  GroupOptionDto,
  GroupUpdate,
  Paged,
  UserCreateInput,
  UserDto,
  UserLevel,
  UserOption,
  UserUpdateInput,
} from '@app/shared';
import { api } from '../../lib/api';
import type { Query } from '../../lib/api';

/* --------------------------------- Groups --------------------------------- */

export const useGroups = (params: Query) =>
  useQuery({
    queryKey: ['user-groups', 'list', params],
    queryFn: () => api.get<Paged<GroupDto>>('/user-groups', params),
    placeholderData: (prev) => prev,
  });

export const useGroup = (id: string | undefined) =>
  useQuery({
    queryKey: ['user-groups', 'one', id],
    queryFn: () => api.get<GroupDto>(`/user-groups/${id}`),
    enabled: Boolean(id),
  });

export const useGroupOptions = (enabled = true) =>
  useQuery({
    queryKey: ['user-groups', 'options'],
    queryFn: () => api.get<{ data: GroupOptionDto[] }>('/user-groups/options'),
    select: (r) => r.data,
    enabled,
  });

/** Every group mutation invalidates all group queries; lists are small. */
function useGroupMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user-groups'] }),
  });
}

export const useCreateGroup = () =>
  useGroupMutation((input: GroupInput) => api.post<GroupDto>('/user-groups', input));
export const useUpdateGroup = () =>
  useGroupMutation(({ id, input }: { id: string; input: GroupUpdate }) =>
    api.patch<GroupDto>(`/user-groups/${id}`, input),
  );
export const useCopyGroup = () =>
  useGroupMutation(({ id, name }: { id: string; name: string }) =>
    api.post<GroupDto>(`/user-groups/${id}/copy`, { name }),
  );
export const useReplaceGroupPermissions = () =>
  useGroupMutation(({ id, sourceGroupId }: { id: string; sourceGroupId: string }) =>
    api.post<GroupDto>(`/user-groups/${id}/replace-permissions`, { sourceGroupId }),
  );
export const useAssignGroups = () =>
  useGroupMutation(({ id, groupIds }: { id: string; groupIds: string[] }) =>
    api.put<GroupDto>(`/user-groups/${id}/assignable`, { groupIds }),
  );
export const useDeleteGroup = () =>
  useGroupMutation((id: string) => api.delete(`/user-groups/${id}`));

/* ---------------------------------- Users --------------------------------- */

export const useUsers = (params: Query, enabled = true) =>
  useQuery({
    queryKey: ['users', 'list', params],
    queryFn: () => api.get<Paged<UserDto>>('/users', params),
    placeholderData: (prev) => prev,
    enabled,
  });

/** Active users of a level the caller can see; with `parentId`, only those directly under it. */
export const useUserOptions = (level: UserLevel, parentId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: ['users', 'options', level, parentId ?? null],
    queryFn: () => api.get<{ data: UserOption[] }>('/users/options', { level, parentId }),
    select: (r) => r.data,
    enabled,
  });

/** Active commission matrices for "Matrix commission" on the add forms. */
export const useMatrixOptions = (enabled = true) =>
  useQuery({
    queryKey: ['users', 'matrix-options'],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string }> }>('/users/matrix-options'),
    select: (r) => r.data,
    enabled,
  });

function useUserMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['users'] }),
        // Member counts on the group list change with users.
        queryClient.invalidateQueries({ queryKey: ['user-groups'] }),
      ]),
  });
}

export const useCreateUser = () =>
  useUserMutation((input: UserCreateInput) => api.post<UserDto>('/users', input));
export const useUpdateUser = () =>
  useUserMutation(({ id, input }: { id: string; input: UserUpdateInput }) =>
    api.patch<UserDto>(`/users/${id}`, input),
  );
export const useSetLocked = () =>
  useUserMutation(({ id, locked }: { id: string; locked: boolean }) =>
    api.post<UserDto>(`/users/${id}/${locked ? 'lock' : 'unlock'}`),
  );
export const useUnlockByLoginId = () =>
  useUserMutation((loginId: string) =>
    api.post<{ loginId: string; wasLocked: boolean }>('/users/unlock', { loginId }),
  );
